import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Loader, PrimaryButton } from "@certn/cascadia-react";
import * as Sentry from "@sentry/react";
import { PropsWithChildren, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AuthHeading } from "./AuthHeading";
import { AuthLayout } from "./AuthLayout";
import { LanguageSelector } from "./LanguageSelector";
import { useAuth } from "./hooks/useAuth";

interface Props extends PropsWithChildren {
  title?: string;
  subtitle?: string;
  actionText?: string;
  email?: string;
  hasApplicantAccount?: boolean;
}

export const AuthOutlet = (props: Props) => {
  // Store subject_id in localStorage BEFORE the HOC wrapper to handle automatic redirects
  // This runs at component mount time, before withAuthenticationRequired can redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const subjectId =
      searchParams.get("subject_id") || searchParams.get("person_id");
    if (subjectId) {
      localStorage.setItem("pending_subject_id", subjectId);
    }
  }, []);

  const InnerAuthOutlet = (innerAuthOutletProps: Props) => {
    const { t, i18n } = useTranslation();
    const {
      title = t("Welcome to Certn"),
      subtitle,
      actionText = t("Continue"),
    } = innerAuthOutletProps;

    const { user, isAuthenticated, loginWithRedirect, isLoading } = useAuth();

    useEffect(() => {
      Sentry.setUser({
        email: user?.email,
        ip_address: "{{auto}}",
        id: user?.sub,
      });
      return () => {
        Sentry.setUser(null);
      };
    }, [user]);

    if (isLoading) {
      return <Loader overlay />;
    }

    if (!isAuthenticated) {
      return (
        <AuthLayout>
          <LanguageSelector />
          <AuthHeading title={title} subtitle={subtitle} />
          <PrimaryButton
            fullWidth
            onPress={async () => {
              // Store subject_id in localStorage before redirect to preserve it through Auth0 flow
              const searchParams = new URLSearchParams(window.location.search);
              const subjectId =
                searchParams.get("subject_id") || searchParams.get("person_id");
              const returnTo =
                window.location.pathname + window.location.search;

              if (subjectId) {
                localStorage.setItem("pending_subject_id", subjectId);
              }

              await loginWithRedirect({
                authorizationParams: {
                  prompt: "login",
                  ui_locales: i18n.language,
                },
                appState: {
                  returnTo,
                },
              });
            }}
            size="large"
            testID="continue-button"
          >
            {actionText}
          </PrimaryButton>
        </AuthLayout>
      );
    }

    return <>{innerAuthOutletProps.children}</>;
  };

  const WrappedAuthOutlet = useMemo(() => {
    const returnTo = window.location.pathname + window.location.search;

    return withAuthenticationRequired(InnerAuthOutlet, {
      onRedirecting: () => <Loader overlay />,
      loginOptions: {
        authorizationParams: {
          screen_hint:
            props.hasApplicantAccount === undefined
              ? "login"
              : !props.hasApplicantAccount && props.email
                ? "signup"
                : "login",
          login_hint: props.email,
        },
        appState: {
          returnTo,
        },
      },
    });
  }, [props.hasApplicantAccount, props.email]);

  return <WrappedAuthOutlet {...props} />;
};
